import React, { useState } from 'react';
import { Alert, Button, Spin, Upload } from 'antd';
import { toast } from 'react-toastify';
import {
  FloatingLabelInput,
  FloatingLabelSelect,
} from '../../elements/floatingLabelInput/FloatingLabelInput';
import {
  LANG_SECTIONS,
  DURATION_OPTIONS,
  WEIGHT_RANGE_OPTIONS,
  SUBCATEGORY_TABS,
  subcategoryFor,
  tagsForCategories,
  withCategory,
} from '../../../utils/protocol-form.util';
import { useProtocolCategories } from '../../../hooks/useProtocolCategories';
import FloatingMultiSelect from '../FloatingMultiSelect';
import SubcategoryPicker from '../SubcategoryPicker';
import TranslateButton from '../../elements/TranslateButton/TranslateButton';
import { useTranslateFields } from '../../../hooks/useTranslateFields';
import styles from '../../../styles/mobileInfoPage.module.scss';
import uploadStyles from '../../../styles/editAnnoucement.module.scss';

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export default function ProtocolBasicInfoStep({
  mode,
  formData,
  setFormData,
  subcategoryOptions,
  onSubcategoriesLoaded,
}) {
  const [uploadingImage, setUploadingImage] = useState(false);
  const categoryOptions = useProtocolCategories();
  const allCategoriesSelected = categoryOptions.every((c) =>
    formData.categories.includes(c.value),
  );

  // Deselecting a category also clears its tag, so the form never holds a
  // tag whose category isn't selected.
  const setCategories = (categories) =>
    setFormData((prev) => ({
      ...prev,
      categories,
      subcategories: tagsForCategories(prev, categories, subcategoryOptions),
    }));

  // Picking a tag selects its category.
  const setSubcategory = (category, id) =>
    setFormData((prev) => ({
      ...prev,
      subcategories: [
        ...(prev.subcategories || []).filter(
          (existing) =>
            subcategoryOptions.find((o) => o.value === existing)?.category !== category,
        ),
        ...(id ? [id] : []),
      ],
      categories: id ? withCategory(prev.categories, category) : prev.categories,
    }));

  const categoryLabel = (category) =>
    categoryOptions.find((c) => c.value === category)?.label || category;

  const setName = (lang, val) =>
    setFormData((prev) => ({ ...prev, name: { ...prev.name, [lang]: val } }));

  const { translating, translate } = useTranslateFields();

  const handleTranslate = async () => {
    if (!formData.name.sr?.trim()) {
      toast.error('Popunite naziv na srpskom pre prevođenja');
      return;
    }

    const result = await translate({ name: formData.name.sr });
    if (!result) return;

    setName('hu', result.hu.name);
    setName('en', result.en.name);

    toast.success('Prevod je uspešno primenjen');
  };

  const handleImageUpload = async (file) => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Dozvoljen je samo JPG, PNG ili WebP format');
      return false;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('Slika je prevelika. Maksimalna veličina je 5MB');
      return false;
    }

    setUploadingImage(true);
    try {
      const base64 = await fileToBase64(file);
      setFormData((prev) => ({ ...prev, imageUrl: base64 }));
      toast.success('Slika je uspešno učitana');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Greška pri učitavanju slike');
    } finally {
      setUploadingImage(false);
    }

    return false; // Prevent antd's default upload behavior
  };

  const handleDeleteImage = () => {
    setFormData((prev) => ({ ...prev, imageUrl: '' }));
  };

  return (
    <div>
      {mode === 'edit' && (
        <Alert
          style={{ marginBottom: '24px' }}
          type="info"
          showIcon
          message="Čuvanjem izmena kreira se nova verzija protokola — korisnici koji su već pokrenuli program ostaju na trenutnoj verziji."
        />
      )}

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Prikaz u aplikaciji</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '32px', alignItems: 'start' }}>
          <div>
            <FloatingMultiSelect
              label="Kategorije"
              placeholder="Izaberite kategorije"
              value={formData.categories}
              options={categoryOptions}
              onChange={setCategories}
            />
            <Button
              type="link"
              style={{ paddingLeft: 0 }}
              onClick={() =>
                setCategories(allCategoriesSelected ? [] : categoryOptions.map((c) => c.value))
              }
            >
              {allCategoriesSelected ? 'Poništi sve' : 'Sve kategorije'}
            </Button>
          </div>
          <FloatingLabelInput
            label="Redosled (1 = prvi, opciono)"
            type="number"
            min={1}
            value={formData.rank ?? ''}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10);
              setFormData((prev) => ({
                ...prev,
                rank: Number.isInteger(parsed) && parsed >= 1 ? parsed : null,
              }));
            }}
          />
        </div>
      </section>

      <section className={styles.section}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
          {SUBCATEGORY_TABS.map(({ category }) => {
            const selected = subcategoryFor(formData.subcategories, category, subcategoryOptions);
            return (
              <SubcategoryPicker
                key={category}
                category={category}
                label={`${categoryLabel(category)} (opciono)`}
                value={selected}
                selectedOption={subcategoryOptions.find((o) => o.value === selected)}
                onChange={(val) => setSubcategory(category, val)}
                onItemsLoaded={onSubcategoriesLoaded}
              />
            );
          })}
        </div>
      </section>

      <section className={styles.section}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
          <FloatingLabelSelect
            label="Trajanje programa"
            placeholder="Izaberite trajanje"
            items={DURATION_OPTIONS}
            value={formData.durationDays}
            disabled={mode === 'edit'}
            onChange={(val) => setFormData((prev) => ({ ...prev, durationDays: val }))}
          />
          <FloatingMultiSelect
            label="Opsezi težine"
            placeholder="Izaberite opsege težine"
            value={formData.weightRanges}
            options={WEIGHT_RANGE_OPTIONS}
            disabled={mode === 'edit'}
            onChange={(val) => setFormData((prev) => ({ ...prev, weightRanges: val }))}
          />
        </div>
      </section>

      <section className={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className={styles.sectionTitle}>Naziv protokola</h3>
          <TranslateButton onClick={handleTranslate} loading={translating} />
        </div>
        <div className={styles.fieldStack}>
          {LANG_SECTIONS.map(({ key, label }) => (
            <FloatingLabelInput
              key={key}
              label={`Naziv — ${label}`}
              value={formData.name[key]}
              onChange={(e) => setName(key, e.target.value)}
              placeholder={`${label.toLowerCase()} naziv`}
            />
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <FloatingLabelInput
          label="Napomena o verziji (opciono)"
          value={formData.versionNotes}
          onChange={(e) => setFormData((prev) => ({ ...prev, versionNotes: e.target.value }))}
        />
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Priloži fotografiju</h3>
        <div className={uploadStyles.photoUploadContainer}>
          <div className={uploadStyles.previewWrapper}>
            {formData.imageUrl ? (
              <>
                <img
                  src={formData.imageUrl}
                  alt="Preview"
                  className={uploadStyles.imagePreview}
                />
                <button
                  className={uploadStyles.deleteBtn}
                  onClick={handleDeleteImage}
                  type="button"
                  disabled={uploadingImage}
                >
                  <span className={uploadStyles.trashIcon}>🗑️</span>
                </button>
              </>
            ) : (
              <img src="/logo.svg" alt="Placeholder" className={uploadStyles.imagePreview} />
            )}
          </div>
          <Upload
            accept="image/jpeg,image/jpg,image/png,image/webp"
            beforeUpload={handleImageUpload}
            showUploadList={false}
            disabled={uploadingImage}
            className={uploadStyles.uploadBoxWrapper}
          >
            <div className={uploadStyles.uploadBox}>
              <div className={uploadStyles.uploadContent}>
                <div className={uploadStyles.cloudIconContainer}>
                  {uploadingImage ? <Spin /> : <span className={uploadStyles.cloudIcon}>☁️</span>}
                </div>
                <p className={uploadStyles.uploadText}>
                  <span className={uploadStyles.highlight}>
                    {uploadingImage ? 'Učitavanje...' : 'Kliknite za otpremanje slike'}
                  </span>{' '}
                  ili prevuci i ispusti
                </p>
                <small className={uploadStyles.fileTypes}>.JPG, .PNG, .WebP (max. 5MB)</small>
              </div>
            </div>
          </Upload>
        </div>
      </section>
    </div>
  );
}
